export const COLLECTION_CURSOR_DURATION = 1600;
export const COLLECTION_CURSOR_FRAME_COUNT = 24;
export const COLLECTION_CURSOR_ANIMATIONS = Object.freeze({
  expand: 'collection-cursor-expand',
  collapse: 'collection-cursor-collapse',
});

const THEMES = {
  light: { ink: '#000000', outline: '#FFFFFF' },
  dark: { ink: '#FFFFFF', outline: '#000000' },
};
const AXES = { vertical: 0, left: -45, right: 45 };
const ARROWS = {
  expand: [
    [[12, 12], [16, 8], [20, 12]],
    [[12, 20], [16, 24], [20, 20]],
  ],
  collapse: [
    [[12, 7], [16, 11], [20, 7]],
    [[12, 25], [16, 21], [20, 25]],
  ],
};

const number = (value) => String(Number(value.toFixed(3)));

function validate(direction, theme, axis) {
  if (!Object.hasOwn(ARROWS, direction)) throw new TypeError('Unknown collection cursor direction');
  if (!Object.hasOwn(THEMES, theme)) throw new TypeError('Unknown collection cursor theme');
  if (!Object.hasOwn(AXES, axis)) throw new TypeError('Unknown collection cursor axis');
}

function createSvg(direction, theme, travel, axis = 'vertical') {
  const { ink, outline } = THEMES[theme];
  const directionSign = direction === 'expand' ? -1 : 1;
  const path = ARROWS[direction].map((points, arrowIndex) => {
    const offset = travel * directionSign * (arrowIndex === 0 ? 1 : -1);
    return points.map(([x, y], pointIndex) => {
      const command = pointIndex === 0 ? 'M' : 'L';
      return `${command}${number(x)} ${number(y + offset)}`;
    }).join('');
  }).join('');

  const rotation = AXES[axis] ? ` transform="rotate(${AXES[axis]} 16 16)"` : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><g${rotation} fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="${path}" stroke="${outline}" stroke-width="3.25"/><path d="${path}" stroke="${ink}" stroke-width="1.75"/><path d="M13.5 16H18.5" stroke="${outline}" stroke-width="2.9"/><path d="M13.5 16H18.5" stroke="${ink}" stroke-width="1.4"/></g></svg>`;
}

function cursorValue(svg) {
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 16 16, pointer`;
}

export function createCollectionCursorFrames(direction, theme = 'light', axis = 'vertical') {
  validate(direction, theme, axis);
  return Array.from({ length: COLLECTION_CURSOR_FRAME_COUNT }, (_, index) => {
    const progress = index / COLLECTION_CURSOR_FRAME_COUNT;
    // The chevrons gently open or close around a fixed row marker. Keeping
    // the complete symbol visible makes the action readable throughout.
    const travel = 1.75 * (1 - Math.cos(progress * Math.PI * 2)) / 2;
    const svg = createSvg(direction, theme, travel, axis);
    return { progress, travel, svg, cursor: cursorValue(svg) };
  });
}

export function createCollectionCursorStyles() {
  const variants = Object.keys(AXES).flatMap(axis => Object.keys(ARROWS).map(direction => ({
    axis, direction, key: axis === 'vertical' ? direction : `${direction}-${axis}`,
  })));
  const themeRules = Object.keys(THEMES).map((theme) => {
    const values = variants.flatMap(({direction, axis, key}) => {
      const start = `--collection-${key}-cursor-start: ${cursorValue(createSvg(direction, theme, 0, axis))};`;
      const frames = createCollectionCursorFrames(direction, theme, axis).map((frame, index) =>
        `--collection-${key}-frame-${index}: ${frame.cursor};`,
      );
      return [start, ...frames];
    });
    const selector = theme === 'light' ? ':root' : 'html.dark-mode, html:has(body.dark-mode), body.dark-mode';
    return `${selector} {\n  ${values.join('\n  ')}\n}`;
  });

  const animations = variants.map(({key}) => {
    const frames = Array.from({ length: COLLECTION_CURSOR_FRAME_COUNT }, (_, index) =>
      `${number(index * 100 / COLLECTION_CURSOR_FRAME_COUNT)}% { --collection-toggle-cursor: var(--collection-${key}-frame-${index}); }`,
    );
    frames.push(`100% { --collection-toggle-cursor: var(--collection-${key}-frame-0); }`);
    return `@keyframes collection-cursor-${key} {\n  ${frames.join('\n  ')}\n}`;
  });

  const axisRules = variants.filter(({axis}) => axis !== 'vertical').map(({axis, direction, key}) => `
    html.interactive-cursor-lock:has(.collection-browser.is-transition-${direction}[data-collection-transition-axis="${axis}"]) {
      --collection-transition-cursor: var(--collection-${key}-cursor-start);
    }

    #app .collection-browser [data-collection-toggle="${direction}"][data-collection-axis="${axis}"],
    html.interactive-cursor-lock:has([data-collection-toggle="${direction}"][data-collection-axis="${axis}"]:hover)::view-transition {
      --collection-toggle-cursor: var(--collection-${key}-cursor-start);
      --collection-cursor-animation: collection-cursor-${key};
    }`);
  return [...themeRules, ...animations, `@media (min-width: 661px) and (hover: hover) and (pointer: fine) {${axisRules.join('\n')}\n}`].join('\n\n');
}
